import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

interface SiteThemeColors {
  background?: string;
  foreground?: string;
  primary?: string;
  primaryForeground?: string;
  secondary?: string;
  accent?: string;
  muted?: string;
  mutedForeground?: string;
}

interface SiteThemeSettings {
  themeId: string;
  displayFont?: string;
  bodyFont?: string;
  customColors?: SiteThemeColors | null;
}

// Site theme presets (matching the ones in SettingsSiteTheme.tsx)
const siteThemePresets = [
  {
    id: "miahma-default",
    name: "Miahma Original",
    displayFont: "'Montserrat', system-ui, sans-serif",
    bodyFont: "'Montserrat', system-ui, sans-serif",
    colors: {
      background: "0 0% 100%",
      foreground: "0 0% 9%",
      primary: "0 0% 9%",
      primaryForeground: "0 0% 100%",
      secondary: "0 0% 96%",
      accent: "0 0% 94%",
      muted: "0 0% 96%",
      mutedForeground: "0 0% 42%",
    }
  },
  {
    id: "elegant-dark",
    name: "Elegante Escuro",
    displayFont: "'Playfair Display', Georgia, serif",
    bodyFont: "'Montserrat', system-ui, sans-serif",
    colors: {
      background: "220 10% 8%",
      foreground: "40 20% 95%",
      primary: "15 50% 60%",
      primaryForeground: "220 10% 10%",
      secondary: "25 20% 25%",
      accent: "25 20% 20%",
      muted: "220 10% 18%",
      mutedForeground: "40 15% 65%",
    }
  },
  {
    id: "modern-minimal",
    name: "Moderno Minimalista",
    displayFont: "'Inter', system-ui, sans-serif",
    bodyFont: "'Inter', system-ui, sans-serif",
    colors: {
      background: "0 0% 100%",
      foreground: "0 0% 10%",
      primary: "0 0% 15%",
      primaryForeground: "0 0% 100%",
      secondary: "0 0% 96%",
      accent: "0 0% 92%",
      muted: "0 0% 96%",
      mutedForeground: "0 0% 45%",
    }
  },
  {
    id: "warm-terracotta",
    name: "Terracota Quente",
    displayFont: "'Lora', Georgia, serif",
    bodyFont: "'DM Sans', system-ui, sans-serif",
    colors: {
      background: "35 30% 95%",
      foreground: "25 20% 15%",
      primary: "18 60% 50%",
      primaryForeground: "35 30% 95%",
      secondary: "30 25% 85%",
      accent: "40 35% 88%",
      muted: "30 20% 90%",
      mutedForeground: "25 15% 45%",
    }
  },
  {
    id: "soft-blush",
    name: "Rosa Suave",
    displayFont: "'Playfair Display', Georgia, serif",
    bodyFont: "'Poppins', system-ui, sans-serif",
    colors: {
      background: "350 30% 97%",
      foreground: "350 10% 15%",
      primary: "350 60% 60%",
      primaryForeground: "350 30% 97%",
      secondary: "350 25% 90%",
      accent: "350 20% 92%",
      muted: "350 15% 93%",
      mutedForeground: "350 10% 50%",
    }
  },
];

interface SiteThemeContextType {
  isLoading: boolean;
  settings: SiteThemeSettings | null;
}

const SiteThemeContext = createContext<SiteThemeContextType>({
  isLoading: true,
  settings: null,
});

export const SiteThemeProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState<SiteThemeSettings | null>(null);
  const settingsRef = useRef<SiteThemeSettings | null>(null);

  useEffect(() => {
    loadAndApplyTheme();
  }, []);

  useEffect(() => {
    const handler = () => {
      // Re-fetch to ensure we apply the latest saved theme (e.g., after leaving /admin).
      loadAndApplyTheme();
    };

    window.addEventListener("site-theme:reapply", handler);
    return () => window.removeEventListener("site-theme:reapply", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAndApplyTheme = async () => {
    try {
      const { data, error } = await supabase
        .from("admin_settings")
        .select("value")
        .eq("key", "site_theme")
        .maybeSingle();

      if (error && error.code !== "PGRST116") {
        console.error("Error loading site theme:", error);
        return;
      }

      if (data?.value) {
        const themeSettings = data.value as unknown as SiteThemeSettings;
        settingsRef.current = themeSettings;
        setSettings(themeSettings);
        applyTheme(themeSettings);
      }
    } catch (error) {
      console.error("Error loading site theme:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadGoogleFont = (fontFamily: string) => {
    // Extract font name from CSS font-family string e.g. "'Lora', Georgia, serif" -> "Lora"
    const match = fontFamily.match(/^'([^']+)'/);
    if (!match) return;
    const fontName = match[1];
    // Skip fonts already loaded in index.html
    const preloadedFonts = ["Playfair Display", "Montserrat"];
    if (preloadedFonts.includes(fontName)) return;
    // Check if already loaded
    const linkId = `dynamic-font-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
    if (document.getElementById(linkId)) return;
    const link = document.createElement("link");
    link.id = linkId;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}:wght@300;400;500;600;700&display=swap`;
    document.head.appendChild(link);
  };

  const applyTheme = (themeSettings: SiteThemeSettings) => {
    // Nunca sobrescrever o tema do painel: se estamos em /admin, guardamos as
    // configurações e elas serão aplicadas quando sair do admin
    // (evento "site-theme:reapply").
    if (window.location.pathname.startsWith("/admin")) return;

    const root = document.documentElement;
    
    
    // Find the base theme preset
    const baseTheme = siteThemePresets.find(t => t.id === themeSettings.themeId) || siteThemePresets[0];
    
    // Merge colors: base theme + custom overrides
    const finalColors = {
      ...baseTheme.colors,
      ...(themeSettings.customColors || {}),
    };

    // Apply fonts (dynamically load if not preloaded)
    const displayFont = themeSettings.displayFont || baseTheme.displayFont;
    const bodyFont = themeSettings.bodyFont || baseTheme.bodyFont;
    
    loadGoogleFont(displayFont);
    loadGoogleFont(bodyFont);
    
    root.style.setProperty("--font-display", displayFont);
    root.style.setProperty("--font-body", bodyFont);

    // Apply colors
    if (finalColors.background) root.style.setProperty("--background", finalColors.background);
    if (finalColors.foreground) root.style.setProperty("--foreground", finalColors.foreground);
    if (finalColors.primary) root.style.setProperty("--primary", finalColors.primary);
    if (finalColors.primaryForeground) root.style.setProperty("--primary-foreground", finalColors.primaryForeground);
    if (finalColors.secondary) root.style.setProperty("--secondary", finalColors.secondary);
    if (finalColors.accent) root.style.setProperty("--accent", finalColors.accent);
    if (finalColors.muted) root.style.setProperty("--muted", finalColors.muted);
    if (finalColors.mutedForeground) root.style.setProperty("--muted-foreground", finalColors.mutedForeground);

    // Also apply to card and popover for consistency
    if (finalColors.background) {
      root.style.setProperty("--card", finalColors.background);
      root.style.setProperty("--popover", finalColors.background);
    }
    if (finalColors.foreground) {
      root.style.setProperty("--card-foreground", finalColors.foreground);
      root.style.setProperty("--popover-foreground", finalColors.foreground);
    }
  };

  return (
    <SiteThemeContext.Provider value={{ isLoading, settings }}>
      {children}
    </SiteThemeContext.Provider>
  );
};

export const useSiteTheme = () => useContext(SiteThemeContext);
